import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Info, Check, Shield, AlertTriangle, RefreshCw, X, CheckCircle2, ChevronRight, Calendar } from 'lucide-react';
import { useAuth } from './AuthContext';
import { API_URL } from '../../api/config';

const RAZORPAY_KEY_ID = 'rzp_test_gipoJjrJig1ZGh';

const PLANS = {
  '6-months': {
    name: '6-Month Premium',
    price: 3899,
    trialDays: 7,
    duration: '6 months',
    savings: 'Save ₹1,095',
    setupFee: 2,
    features: [
      'All premium features',
      'Expense & Sales reports',
      'Advanced analytics',
      'Data visualization',
      'Email support'
    ]
  },
  '1-year': {
    name: 'Annual Premium',
    price: 4999,
    trialDays: 10,
    duration: '12 months',
    savings: 'Save ₹2,989 - Best Value!',
    setupFee: 2,
    popular: true,
    features: [
      'All premium features',
      'Expense & Sales reports',
      'Advanced analytics',
      'Data visualization',
      'Priority support',
      'Extended trial period'
    ]
  }
};

const PlanCard = ({ planKey, planDetails, onStartTrial, onSubscribe, startingTrial, processingPayment, selectedPlan, canStartTrial }) => {
  return (
    <div className={`relative flex flex-col p-8 rounded-[2.5rem] bg-white transition-all duration-300 h-full ${
      planDetails.popular 
        ? 'border-2 border-[#2563eb] shadow-2xl shadow-blue-100 scale-105 z-10' 
        : 'border border-slate-100 shadow-sm'
    }`}>
      {planDetails.popular && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#2563eb] text-white text-[10px] font-black px-5 py-1.5 rounded-full uppercase tracking-widest shadow-lg">
          Most Popular
        </div>
      )}

      <div className="mb-8">
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-xl font-black text-slate-900 tracking-tight">{planDetails.name}</h3>
          {planDetails.savings && (
            <span className="bg-blue-50 text-blue-600 px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-tight">
              {planDetails.savings}
            </span>
          )}
        </div>
        
        <div className="flex items-baseline gap-1">
          <span className="text-4xl font-black text-slate-900 tracking-tighter">₹{planDetails.price}</span>
          <span className="text-sm font-bold text-slate-400">/ {planDetails.duration}</span>
        </div>
        
        <p className="text-sm font-medium text-slate-400 mt-4 leading-relaxed">
          {planDetails.trialDays}-day FREE trial · ₹{planDetails.setupFee} setup fee
        </p>
      </div>

      <div className="flex-1 space-y-4 mb-10">
        {planDetails.features.map((feature, idx) => (
          <div key={idx} className="flex items-start gap-3">
            <CheckCircle2 size={18} className="text-[#2563eb] shrink-0 mt-0.5" />
            <span className="text-sm font-medium text-slate-600">
              {feature}
            </span>
          </div>
        ))}
      </div>

      {canStartTrial && (
        <button 
          onClick={() => onStartTrial(planKey)}
          disabled={startingTrial && selectedPlan === planKey}
          className="w-full py-4 rounded-2xl font-black text-sm transition-all transform active:scale-95 bg-slate-50 text-slate-900 hover:bg-slate-100 mb-3 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
          {startingTrial && selectedPlan === planKey ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-900"></div>
              Starting...
            </>
          ) : (
            `Start ${planDetails.trialDays}-Day FREE Trial`
          )}
        </button>
      )}

      <button 
        onClick={() => onSubscribe(planKey)}
        disabled={processingPayment && selectedPlan === planKey}
        className={`w-full py-4 rounded-2xl font-black text-sm transition-all transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
          planDetails.popular 
            ? 'bg-[#2563eb] text-white hover:bg-blue-700 shadow-xl shadow-blue-100'
            : 'bg-slate-900 text-white hover:bg-slate-800'
        }`}>
        {processingPayment && selectedPlan === planKey ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            Processing...
          </>
        ) : (
          `Subscribe Now`
        )}
      </button>
    </div>
  );
};

const SubscriptionPage = () => {
  const { userToken: token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [startingTrial, setStartingTrial] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundReason, setRefundReason] = useState('');
  const [submittingRefund, setSubmittingRefund] = useState(false);

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/subscription/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch subscription status');
      }
      
      const data = await response.json();
      setCurrentSubscription(data);
      
      if (data.status === 'trial_expired_needs_payment') {
        alert('Your trial has ended. Please complete payment to continue accessing premium features.');
      }
    } catch (error) {
      console.error('Error fetching subscription status:', error);
      alert('Error: Failed to fetch subscription status');
    } finally {
      setLoading(false);
    }
  };

  const handleRefundRequest = async () => {
    if (!refundReason || refundReason.trim().length < 10) {
      alert('Please provide a detailed reason (minimum 10 characters)');
      return;
    }

    try {
      setSubmittingRefund(true);
      const response = await fetch(`${API_URL}/subscription/request-refund`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: refundReason })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to submit refund request');
      }

      alert(data.message);
      setShowRefundModal(false);
      setRefundReason('');
      fetchSubscriptionStatus();
    } catch (error) {
      console.error('Error requesting refund:', error);
      alert('Error: ' + error.message);
    } finally {
      setSubmittingRefund(false);
    }
  };

  const loadRazorpay = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleStartTrial = async (plan) => {
    try {
      setStartingTrial(true);
      setSelectedPlan(plan);
      
      const orderResponse = await fetch(`${API_URL}/subscription/create-trial-setup-order`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ plan })
      });
      
      if (!orderResponse.ok) {
        const error = await orderResponse.json();
        throw new Error(error.message || 'Failed to create setup order');
      }
      
      const orderData = await orderResponse.json();
      await processSetupPayment(orderData, plan);
    } catch (error) {
      console.error('Error starting trial:', error);
      alert('Error: ' + error.message);
      setStartingTrial(false);
      setSelectedPlan(null);
    }
  };

  const processSetupPayment = async (orderData, plan) => {
    try {
      const razorpayLoaded = await loadRazorpay();
      
      if (!razorpayLoaded) {
        throw new Error('Razorpay SDK failed to load');
      }

      const options = {
        key: RAZORPAY_KEY_ID,
        amount: orderData.amount,
        currency: orderData.currency,
        order_id: orderData.orderId,
        name: 'TOBO',
        description: `Setup Auto-pay for ${PLANS[plan].name}`,
        prefill: {
          email: '',
          contact: '',
          name: ''
        },
        theme: { color: '#4F46E5' },
        handler: async function (response) {
          try {
            const verifyResponse = await fetch(`${API_URL}/subscription/verify-trial-setup-payment`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                razorpayOrderId: response.razorpay_order_id,
                razorpayPaymentId: response.razorpay_payment_id,
                razorpaySignature: response.razorpay_signature,
                plan: plan
              })
            });

            if (!verifyResponse.ok) {
              throw new Error('Failed to verify setup payment');
            }
            const verifyData = await verifyResponse.json();
            if (verifyData.success) {
              alert(verifyData.message);
              fetchSubscriptionStatus();
              navigate('/sales-report');
            }
          } catch (error) {
            console.error('Verification error:', error);
            alert('Setup Verification Error: ' + error.message);
          } finally {
            setStartingTrial(false);
            setSelectedPlan(null);
          }
        },
        modal: {
          ondismiss: function() {
            setStartingTrial(false);
            setSelectedPlan(null);
          }
        }
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    } catch (error) {
      console.error('Setup payment error:', error);
      alert('Error: ' + error.message);
      setStartingTrial(false);
      setSelectedPlan(null);
    }
  };

  const handleSubscription = async (plan) => {
    try {
      setProcessingPayment(true);
      setSelectedPlan(plan);
      
      const orderResponse = await fetch(`${API_URL}/subscription/create-order`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ plan })
      });
      
      if (!orderResponse.ok) {
        throw new Error('Failed to create subscription order');
      }
      
      const orderData = await orderResponse.json();
      const razorpayLoaded = await loadRazorpay();
      
      if (!razorpayLoaded) {
        alert('Razorpay SDK failed to load. Please check your connection');
        return;
      }

      const options = {
        description: PLANS[plan].name,
        currency: 'INR',
        key: RAZORPAY_KEY_ID,
        amount: orderData.amount,
        name: 'TOBO',
        order_id: orderData.orderId,
        prefill: {
          email: 'contact.wazwan@gmail.com',
          contact: '+91 7652992906',
          name: 'TOBO'
        },
        theme: { color: '#4F46E5' },
        handler: async function (response) {
          try {
            const paymentResponse = await fetch(`${API_URL}/subscription/verify-payment`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                razorpayOrderId: orderData.orderId,
                razorpayPaymentId: response.razorpay_payment_id,
                razorpaySignature: response.razorpay_signature,
                plan: plan
              })
            });

            if (!paymentResponse.ok) {
              throw new Error('Failed to verify payment');
            }

            const paymentData = await paymentResponse.json();

            if (paymentData.success) {
              alert(`Your ${PLANS[plan].name} has been activated successfully!`);
              fetchSubscriptionStatus();
              navigate('/sales-report');
            }
          } catch (error) {
            console.error('Verification error:', error);
            alert('Payment Verification Error: ' + error.message);
          } finally {
            setProcessingPayment(false);
            setSelectedPlan(null);
          }
        },
        modal: {
          ondismiss: function() {
            setProcessingPayment(false);
            setSelectedPlan(null);
          }
        }
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    } catch (error) {
      console.error('Subscription error:', error);
      alert('Error: ' + (error.message || 'Failed to subscribe'));
      setProcessingPayment(false);
      setSelectedPlan(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-[#F8FAFC] items-center justify-center p-5">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-slate-400 font-medium">Loading subscription details...</p>
        </div>
      </div>
    );
  }

  // Active Subscription View
  if (currentSubscription && (currentSubscription.status === 'trial' || currentSubscription.status === 'active')) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] p-12 pb-32 animate-in fade-in duration-700">
        <div className="max-w-5xl mx-auto space-y-12">
          <nav className="flex items-center gap-2 text-xs font-bold text-slate-400">
            <span className="hover:text-slate-600 cursor-pointer">Settings</span>
            <ChevronRight size={14} />
            <span className="text-slate-900">Subscription</span>
          </nav>

          <div>
            <h1 className="text-4xl font-black text-slate-900 tracking-tight mb-2">Active Plan</h1>
            <p className="text-base font-medium text-slate-400">Manage your billing and subscription preferences.</p>
          </div>

          <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden">
            <div className="p-10">
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-6 mb-10">
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <h2 className="text-2xl font-black text-slate-900 tracking-tight">{currentSubscription.planName}</h2>
                    <span className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest ${
                      currentSubscription.status === 'trial' 
                        ? 'bg-green-50 text-green-600' 
                        : 'bg-emerald-50 text-emerald-600'
                    }`}>
                      {currentSubscription.status === 'trial' ? 'Trial Active' : 'Active'}
                    </span>
                  </div>
                  <p className="text-sm font-bold text-slate-400 flex items-center gap-2">
                    {currentSubscription.status === 'trial' 
                      ? `${currentSubscription.daysRemaining} days remaining in trial`
                      : 'Automatically renews'}
                  </p>
                </div>
                {currentSubscription.refundEligibility?.eligible && (
                  <button 
                    onClick={() => setShowRefundModal(true)}
                    className="px-8 py-3.5 border-2 border-rose-100 text-rose-500 rounded-2xl font-black text-xs hover:bg-rose-50 transition-all">
                    Request Refund
                  </button>
                )}
              </div>

              <div className="h-px bg-slate-50 mb-10"></div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                <div className="space-y-3">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                    {currentSubscription.status === 'trial' ? 'Trial Ends' : 'Next Renewal'}
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center">
                      <Calendar size={18} />
                    </div>
                    <span className="text-lg font-black text-slate-900">
                      {formatDate(currentSubscription.status === 'trial' ? currentSubscription.trialEndDate : currentSubscription.endDate)}
                    </span>
                  </div>
                </div>
                {currentSubscription.status === 'active' && (
                  <div className="space-y-3">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Days Remaining</p>
                    <div className="flex items-center gap-3">
                      <span className="text-3xl font-black text-slate-900 tracking-tighter">{currentSubscription.daysRemaining}</span>
                    </div>
                  </div>
                )}
              </div>

              {currentSubscription.refundEligibility?.eligible && (
                <div className="mt-8 bg-blue-50 border border-blue-200 rounded-2xl p-6">
                  <p className="text-blue-800 text-sm font-bold mb-1">Refund Policy:</p>
                  <p className="text-blue-700 text-sm">{currentSubscription.refundEligibility.reason}</p>
                  <p className="text-blue-900 text-sm font-black mt-2">
                    Refund Amount: ₹{currentSubscription.refundEligibility.refundAmount?.toFixed(2)}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Refund Status Views
  if (currentSubscription?.status === 'refund_requested') {
    return (
      <div className="min-h-screen bg-[#F8FAFC] p-12">
        <div className="max-w-3xl mx-auto">
          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-[2.5rem] p-10 shadow-sm">
            <div className="flex gap-4">
              <RefreshCw className="text-yellow-700 flex-shrink-0 mt-1" size={28} />
              <div>
                <h3 className="text-yellow-900 font-black text-xl mb-3">Refund Request Pending</h3>
                <p className="text-yellow-800 text-sm mb-4 font-medium">
                  Your refund request is being processed. We'll notify you once it's completed.
                </p>
                {currentSubscription.refundRequest && (
                  <div className="text-yellow-800 text-sm space-y-2 font-medium">
                    <p><span className="font-black">Requested:</span> {formatDate(currentSubscription.refundRequest.requestedAt)}</p>
                    <p><span className="font-black">Amount:</span> ₹{currentSubscription.refundRequest.refundAmount?.toFixed(2)}</p>
                    <p><span className="font-black">Status:</span> {currentSubscription.refundRequest.status}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (currentSubscription?.status === 'refunded') {
    return (
      <div className="min-h-screen bg-[#F8FAFC] p-12">
        <div className="max-w-3xl mx-auto">
          <div className="bg-green-50 border-2 border-green-200 rounded-[2.5rem] p-10 shadow-sm">
            <div className="flex gap-4">
              <Check className="text-green-700 flex-shrink-0 mt-1" size={28} />
              <div>
                <h3 className="text-green-900 font-black text-xl mb-3">Refund Completed</h3>
                <p className="text-green-800 text-sm mb-4 font-medium">
                  Your refund has been processed successfully. The amount will be credited to your account within 5-7 business days.
                </p>
                {currentSubscription.refundRequest && (
                  <div className="text-green-800 text-sm space-y-2 font-medium">
                    <p><span className="font-black">Processed:</span> {formatDate(currentSubscription.refundRequest.processedAt)}</p>
                    <p><span className="font-black">Amount:</span> ₹{currentSubscription.refundRequest.refundAmount?.toFixed(2)}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Choose Plan View
  return (
    <div className="min-h-screen bg-[#F8FAFC] p-12 pb-24 animate-in fade-in duration-700">
      <div className="max-w-7xl mx-auto">
        <nav className="flex items-center gap-2 text-xs font-bold text-slate-400 mb-8">
          <span className="hover:text-slate-600 cursor-pointer">Settings</span>
          <ChevronRight size={14} />
          <span className="text-slate-900">Subscription</span>
        </nav>

        <div className="mb-16">
          <h1 className="text-5xl font-black text-slate-900 tracking-tighter mb-4">Choose Your Plan</h1>
          <p className="text-lg font-medium text-slate-400 max-w-2xl leading-relaxed">
            Start with a free trial, then continue with full access to premium features.
          </p>
        </div>

        {currentSubscription?.status === 'trial_expired_needs_payment' && (
          <div className="bg-red-50 border-2 border-red-200 rounded-[2.5rem] p-8 mb-10 shadow-sm">
            <div className="flex gap-4">
              <AlertTriangle className="text-red-700 flex-shrink-0 mt-1" size={24} />
              <div>
                <h3 className="text-red-900 font-black text-lg mb-2">Trial Expired - Payment Required</h3>
                <p className="text-red-800 text-sm mb-4 font-medium">
                  Your free trial has ended. Complete payment below to continue accessing premium features.
                </p>
                <button 
                  className="bg-red-600 hover:bg-red-700 text-white py-3 px-8 rounded-2xl font-black text-sm transition-all"
                  onClick={() => handleSubscription(currentSubscription.plan)}
                >
                  Pay Now - ₹{currentSubscription.amount}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="bg-blue-50 border-2 border-blue-200 rounded-[2.5rem] p-8 mb-12 shadow-sm">
          <div className="flex gap-4">
            <Shield className="text-blue-700 flex-shrink-0 mt-1" size={24} />
            <div>
              <h3 className="text-blue-900 font-black text-lg mb-3">How It Works</h3>
              <div className="text-blue-800 text-sm space-y-2 font-medium">
                <div className="flex items-start gap-2">
                  <span className="font-black">1.</span>
                  <p>Pay <span className="font-black">₹2</span> to verify your payment method and activate auto-pay</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-black">2.</span>
                  <p>Get immediate access to Sales Report & Expense Report during your free trial</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-black">3.</span>
                  <p>After trial ends, you'll be charged automatically to continue access</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-black">4.</span>
                  <p><span className="font-black">No charges during trial</span> - Cancel anytime before trial ends</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-black">5.</span>
                  <p><span className="font-black">Refund Policy:</span> Full refund within 7 days, 50% refund within 30 days</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch mb-16">
          {Object.entries(PLANS).map(([planKey, planDetails]) => (
            <PlanCard 
              key={planKey}
              planKey={planKey}
              planDetails={planDetails}
              onStartTrial={handleStartTrial}
              onSubscribe={handleSubscription}
              startingTrial={startingTrial}
              processingPayment={processingPayment}
              selectedPlan={selectedPlan}
              canStartTrial={currentSubscription?.canStartTrial}
            />
          ))}
        </div>

        <div className="bg-amber-50 border-2 border-amber-200 rounded-[2.5rem] p-8 shadow-sm">
          <div className="flex gap-4">
            <Info className="text-amber-700 flex-shrink-0 mt-1" size={20} />
            <div className="space-y-2">
              <p className="text-amber-900 font-black">Important Information:</p>
              <ul className="text-amber-800 space-y-1 text-sm font-medium">
                <li>• ₹2 setup fee verifies payment method and activates auto-pay</li>
                <li>• Free trial gives full access to all premium features</li>
                <li>• After trial: automatic charge to continue (cancel anytime during trial)</li>
                <li>• Full refund within 7 days, 50% refund within 30 days of purchase</li>
                <li>• Access both Sales Report & Expense Report</li>
                <li>• All prices include GST</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {showRefundModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-[2.5rem] shadow-2xl max-w-md w-full p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-black text-slate-900 flex items-center gap-2">
                <RefreshCw className="text-red-600" size={24} />
                Request Refund
              </h3>
              <button
                onClick={() => {
                  setShowRefundModal(false);
                  setRefundReason('');
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                <X size={24} />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-slate-600 text-sm mb-2 font-medium">
                Refund Type: <span className="font-black text-slate-900">
                  {currentSubscription?.refundEligibility?.refundType === 'full' ? 'Full Refund' : '50% Refund'}
                </span>
              </p>
              <p className="text-slate-600 text-sm font-medium">
                Refund Amount: <span className="font-black text-slate-900">₹{currentSubscription?.refundEligibility?.refundAmount}</span>
              </p>
            </div>

            <div className="mb-6">
              <p className="text-slate-600 text-sm font-medium">Reason for Refund:</p>
              <textarea
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                className="w-full border border-slate-200 rounded-lg p-2 text-slate-800"
                rows="4"
              />
            </div>

            <button
onClick={handleRefundRequest}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-2 rounded-lg"
            >
              Request Refund
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
export default SubscriptionPage;